import * as React from "react";
import { toast, ToastOptions } from "react-toastify";
import { QESButton, QESSelect } from "@app/shared/qes-standards";
import { Client } from "../client/client";
import { ProjectService } from "../projects"
import { Resource } from "../resources";
import { JobQueue } from "./job-queues/jobQueue";
import { JobType } from "./jobType";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { JSX } from "react";
import { NestingParentDropdownItem, SingleSelectNestedDropdown } from '@app/shared/checkbox-dropdown';

interface AddJobQueueMapFlyoutProps {
  clients?: Client[];
  jobTypes: { [key: string]: JobType };
  jobQueues: JobQueue[];
  countyItems: NestingParentDropdownItem[];
  onSubmit: (jobQueueId: number, jobType?: JobType, clientId?: number, projectId?: number, countySSA?: string) => void;
}

const AddJobQueueMapFlyout = (props: AddJobQueueMapFlyoutProps): JSX.Element => {
  const projectService = new ProjectService();

  const [clients] = React.useState<Client[]>(props.clients);
  const [jobQueues] = React.useState<JobQueue[]>(props.jobQueues);
  const [jobTypes] = React.useState<{ [key: string]: JobType }>(props.jobTypes);
  const [projects, setProjects] = React.useState<Resource[]>([]);

  const [jobTypeOptions, setJobTypeOptions] = React.useState<JSX.Element[]>([]);
  const [clientOptions, setClientOptions] = React.useState<JSX.Element[]>([]);
  const [queueOptions, setQueueOptions] = React.useState<JSX.Element[]>([]);

  const [jobType, setJobType] = React.useState<JobType>(null);
  const [client, setClient] = React.useState<Client>(null);
  const [project, setProject] = React.useState<Resource>(null);
  const [countySSA, setCountySSA] = React.useState<string>("");
  const [selectedCounty, setSelectedCounty] = React.useState(null);
  const [jobQueue, setJobQueue] = React.useState<JobQueue>(null);
  const [createEnabled, setCreateEnabled] = React.useState<boolean>(false);
  const [projectOptions, setProjectOptions] = React.useState<JSX.Element[]>([]);
  const flyoutContext = React.useContext(FlyoutContext);

  React.useEffect(() => {
    const sortedJobTypes = Object.keys(jobTypes).sort((jt1, jt2) => jt1.localeCompare(jt2));
    const selectedJobType = sortedJobTypes.length > 0 ? jobTypes[sortedJobTypes[0]] : null;
    const localJobTypeOptions: JSX.Element[] = [];

    sortedJobTypes.forEach(key => {
      const option = (
        <option key={key} value={key}>{jobTypes[key]}</option>
      );
      localJobTypeOptions.push(option);
    });
    setJobTypeOptions(localJobTypeOptions);
    setJobType(selectedJobType);

    const localClientOptions: JSX.Element[] = [];
    const selectedClient: Client = null;
    (clients && clients.forEach(client => {
      const option = (
        <option key={client.clientId} value={client.clientId}>{client.name}</option>
      );
      localClientOptions.push(option);
    }));
    setClientOptions(localClientOptions);
    setClient(selectedClient);

    const localJobQueueOptions: JSX.Element[] = [];
    (jobQueues && jobQueues.forEach(queue => {
      const option = (
        <option key={queue.id} value={queue.id}>{queue.name}</option>
      );
      localJobQueueOptions.push(option);
    }));
    setQueueOptions(localJobQueueOptions);
    setJobQueue(null);
  }, [])

  React.useEffect(() => {
    setCreateEnabled(!!jobQueue && (!!jobType || !!client || !!project));
  }, [jobType, client, project, jobQueue])

  React.useEffect(() => {
    const getProjects = async () => {
      const options: JSX.Element[] = [];
      if (!!client) {
        const projects = await projectService.getClientProjectResources(client.clientId);
        setProjects(projects);
        !!projects && projects.forEach(project => {
          const option = (
            <option key={project.resourceId} value={project.resourceId}>{project.name}</option>
          );
          options.push(option);
        });
      }
      setProjectOptions(options);
    };
    getProjects();
  }, [client]);

  const changeClientSelection = (key: number) => {
    const selectedOption: Client = !!key
      ? clients.find((client: Client) => client.clientId === key)
      : null;
    setClient(selectedOption);
  }

  const changeJobTypeSelection = (key: string) => {
    const selectedOption: JobType = !!key
      ? jobTypes[key]
      : null;
    setJobType(selectedOption);
  }

  const changeJobQueueSelection = (key: number) => {
    const selectedOption: JobQueue = !!key
      ? jobQueues.find((jobQueue: JobQueue) => jobQueue.id === key)
      : null;
    setJobQueue(selectedOption);
  }

  const changeProjectSelection = (key: number) => {
    const selectedOption: Resource = !!key
      ? projects.find((project: Resource) => project.resourceId === key)
      : null;
    setProject(selectedOption);
  }

  const handleSubmit = () => {
    try {
      props.onSubmit(jobQueue.id, jobType, client?.clientId, project?.resourceId, countySSA);
      const toastOptions: ToastOptions = {
        closeOnClick: true,
        containerId: "z-index-9999",
      };
      toast.success("Job Queue Map Created", toastOptions);
      flyoutContext.handleCloseFlyout();
    }
    catch { }
  }

  return (
    <>
      <div className="col">
        <div className="row mb5">
          <div className="col">
            <QESSelect
              allowEmpty={true}
              label="Job Type"
              onChange={(e) => changeJobTypeSelection(e.target.value)}
              placeholder="All Job Types"
              testId="add-job-queue-map-flyout_jobType">
              {jobTypeOptions}
            </QESSelect>
            <QESSelect
              allowEmpty={true}
              label="Client"
              onChange={(e) => changeClientSelection(+e.target.value)}
              placeholder="All Clients"
              testId="add-job-queue-map-flyout_client">
              {clientOptions}
            </QESSelect>
            <QESSelect
              allowEmpty={true}
              label="Project"
              onChange={(e) => changeProjectSelection(+e.target.value)}
              placeholder="All Projects"
              testId="add-job-queue-map-flyout_project">
              {projectOptions}
            </QESSelect>
            <QESSelect
              label="Job Queue"
              onChange={(e) => changeJobQueueSelection(+e.target.value)}
              placeholder="Select a Job Queue"
              required={true}
              testId="add-job-queue-map-flyout_jobQueue"
              isValid={!!jobQueue}
            >
              {queueOptions}
            </QESSelect>
            <SingleSelectNestedDropdown
              containerClassName="col p-0"
              items={props.countyItems}
              label="County"
              onSelectionChanged={(item) => {
                setSelectedCounty(item);
                if (!item) {
                  setCountySSA("");
                } else {
                  setCountySSA(item.key);
                }
              }}
              selectedItem={selectedCounty}
              testId="job-queue-map-filters__county"
            />
          </div>
        </div>
      </div>
      <FlyoutFooter
        submitButton={<QESButton qesTestId="add-job-queue-map-flyout_create" qesType="primary" className="mb-3" disabled={!createEnabled} onClick={handleSubmit}>Create</QESButton>}
      />
    </>
  );
}

export default AddJobQueueMapFlyout;